<template>
  <div v-if="isSwitchingContext" class="relative flex-1">
    <div class="banking-context-switch-overlay" role="status" aria-live="polite" aria-busy="true">
      <div class="banking-context-switch-overlay__spinner" aria-hidden="true" />
      <p>{{ t("bankingContext_switchingTo") }}</p>
      <p>{{ targetLabel }}</p>
    </div>
  </div>
  <template v-else><slot /></template>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useIntl } from "@moroku/money-webapp-components";
import { useBankingContextStore } from "@/stores/bankingContext";
import { BankingContextType } from "@/types/bankingContext";

const { t } = useIntl();
const bankingContextStore = useBankingContextStore();

const isSwitchingContext = computed(() => bankingContextStore.isSwitchingContext);

const targetLabel = computed(() => {
  const target = bankingContextStore.switchingTo;
  if (!target) {
    return "";
  }
  if (target.type === BankingContextType.Personal) {
    return t("bankingContext_personal");
  }
  return bankingContextStore.businesses.find(business => business.id === target.entityId)?.name ?? t("bankingContext_business");
});
</script>

<style scoped lang="scss">
.banking-context-switch-overlay {
  @apply absolute inset-0 z-50 flex flex-col items-center bg-surface px-6 pt-32 text-center;

  p {
    @apply m-0;

    &:first-of-type {
      @apply text-base font-normal;
      color: var(--color-text-muted);
    }

    &:last-of-type {
      @apply mt-2 text-xl font-bold;
      color: var(--color-text-primary);
    }
  }
}

.banking-context-switch-overlay__spinner {
  @apply mb-8 h-12 w-12 animate-spin rounded-full border-[0.4rem];
  border-color: var(--color-state-disabled-light);
  border-top-color: var(--color-text-primary);
}
</style>
